import {
  Flex,
  Table, TextSort, TripleToggleInput, useStyledTheme,
} from '@equifinance/shared';
import { useCallback } from 'react';
import styled from 'styled-components';
import { BankAccount } from '../../../../../contexts/brokerages';
import { useCompanyContext } from '../../../../../contexts/company';
import { BrokerDetailGenericSection } from '../../../components/BrokerDetailGenericSection';
import LongContentCell from '../../../components/LongContentCell';

export const bankDetailsRefName = 'bank-details';

export const useTableStyles = () => {
  const theme = useStyledTheme();

  return {
    table: {
      style: {
        maxHeight: `${theme.esisHistoryHeight} !important`,
        overflow: 'auto !important',
      },
    },
    headRow: {
      style: {
        'border-top-right-radius': `${theme.padding[2]}`,
        'border-top-left-radius': `${theme.padding[2]}`,
        backgroundColor: `${theme.colours.neutral[8]} !important`,
        color: `${theme.colours.neutral[2]} !important`,
      },
    },
    rows: {
      style: {
        'font-size': `${theme.fontSize[1]} !important`,
        backgroundColor: `${theme.colours.neutral[9]} !important`,
        color: `${theme.colours.neutral[2]} !important`,
      },
    },
  };
};

export const TripleToggleInputWrapper = styled(Flex)`
  width: 40px;
`;

export default function BankDetailsSection() {
  const { company: { bankAccounts, commercialDetails: { commissionPaymentAccount, feePaymentAccount } } } = useCompanyContext();

  const FeeAccountToggle = useCallback((bankAccount: BankAccount) => {
    const value = bankAccount.id === feePaymentAccount;

    return (
      <TripleToggleInputWrapper>
        <TripleToggleInput
          id={`fee-account-${bankAccount?.id}`}
          value={value}
          onChange={undefined}
        />
      </TripleToggleInputWrapper>
    );
  }, [bankAccounts]);

  const CommissionAccountToggle = useCallback((bankAccount: BankAccount) => {
    const value = bankAccount.id === commissionPaymentAccount;

    return (
      <TripleToggleInputWrapper>
        <TripleToggleInput
          id={`commission-account-${bankAccount?.id}`}
          value={value}
          onChange={undefined}
        />
      </TripleToggleInputWrapper>
    );
  }, [bankAccounts]);

  const columns = [
    {
      name: 'Bank account name',
      cell: (bankAccount: BankAccount) => LongContentCell(bankAccount.bankAccountName, `${bankAccount.id}-bank-account`),
      sortFunction: TextSort<BankAccount>('bankAccountName'),
    },
    {
      name: 'Sort code',
      selector: (bankAccount: BankAccount) => bankAccount.bankSortCode,
      sortFunction: TextSort<BankAccount>('bankSortCode'),
    },
    {
      name: 'Bank account nunmber',
      selector: (bankAccount: BankAccount) => bankAccount.bankAccountNumber,
      sortFunction: TextSort<BankAccount>('bankAccountNumber'),
    },
    {
      name: 'Fee account',
      cell: (bankAccount: BankAccount) => FeeAccountToggle(bankAccount),
    },
    {
      name: 'Commission account',
      cell: (bankAccount: BankAccount) => CommissionAccountToggle(bankAccount),
    },
  ];

  const tableStyles = useTableStyles();

  return (
    <BrokerDetailGenericSection
      id={bankDetailsRefName}
      refName={bankDetailsRefName}
      title="Bank details"
      level={1}
    >
      <Table
        showSearch={false}
        columns={columns}
        data={bankAccounts}
        styles={tableStyles}
      />
    </BrokerDetailGenericSection>
  );
}
